import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {
    NaturalLanguageApplyRequest,
    NaturalLanguageClarificationRequest,
    NaturalLanguageRefinementRequest,
    NaturalLanguageSessionResponse,
    NaturalLanguageSessionStartRequest
} from "../../model/dto/api/natural-language-criteria-session.interface";

/**
 * Service for natural language criteria session management.
 * F-031: Natural Language Selection Criteria.
 *
 * Acceptance Criteria:
 * - Given natural language input like 'customers in California with contracts expiring in 30 days',
 *   when processed, then corresponding criteria are generated
 * - Given generated criteria, when reviewed, then user can modify and refine
 * - Given ambiguous input, when processed, then clarifying questions are presented
 */
@Injectable({
    providedIn: 'root'
})
export class NaturalLanguageCriteriaSessionService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/naturalLanguageCriteriaSessions')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-comments';
    }

    /**
     * Starts a new natural language criteria session.
     * Processes the initial query and returns interpreted criteria.
     */
    startSession(request: NaturalLanguageSessionStartRequest): Observable<NaturalLanguageSessionResponse> {
        return this.postGeneric<NaturalLanguageSessionResponse>(request, '/start');
    }

    /**
     * Refines the criteria with additional natural language input.
     * F-031: Acceptance Criteria - "Given generated criteria, when reviewed,
     * then user can modify and refine"
     */
    refineCriteria(request: NaturalLanguageRefinementRequest): Observable<NaturalLanguageSessionResponse> {
        return this.postGeneric<NaturalLanguageSessionResponse>(request, '/refine');
    }

    /**
     * Provides clarification responses to pending questions.
     * F-031: Acceptance Criteria - "Given ambiguous input, when processed,
     * then clarifying questions are presented"
     */
    provideClarification(request: NaturalLanguageClarificationRequest): Observable<NaturalLanguageSessionResponse> {
        return this.postGeneric<NaturalLanguageSessionResponse>(request, '/clarify');
    }

    /**
     * Finalizes the session criteria.
     */
    finalizeSession(sessionId: number): Observable<NaturalLanguageSessionResponse> {
        return this.postGeneric<NaturalLanguageSessionResponse>({}, `/${sessionId}/finalize`);
    }

    /**
     * Applies the finalized criteria to a campaign.
     */
    applyCriteria(request: NaturalLanguageApplyRequest): Observable<NaturalLanguageSessionResponse> {
        return this.postGeneric<NaturalLanguageSessionResponse>(request, '/apply');
    }

    /**
     * Gets the current state of a session.
     */
    getSession(sessionId: number): Observable<NaturalLanguageSessionResponse> {
        return this.getGeneric<NaturalLanguageSessionResponse>(`/${sessionId}`);
    }

    /**
     * Gets all active sessions for the current user.
     */
    getActiveSessions(): Observable<NaturalLanguageSessionResponse[]> {
        return this.getGeneric<NaturalLanguageSessionResponse[]>('/active');
    }

    /**
     * Abandons a session.
     */
    abandonSession(sessionId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${sessionId}`);
    }
}
